<template>
    <div class="files-container">
        <table class="files-table">
            <thead>
            <tr>
                <th>{{$t("message.name")}}</th>
                <th>{{$t("message.type")}}</th>
                <th>{{$t("message.lastRegulation")}}</th>
                <th>{{$t("message.actions")}}</th>
            </tr>
            </thead>
            <tbody>
            <tr
                v-for="file in files"
                :key="file._id"
            >
                <td>
                    <div>
                        <i :class="getFileIcon(file)" class="file-icon" @click="showViewer(file)"></i>
                        {{ file.name }}
                    </div>
                </td>
                <td>{{ getFileType(file) }}</td>
                <td>{{ formatDate(file.updatedAt) }}</td>
                <td class="actions-column">
                    <button @click="handleDoubleClick(file)" class="action-button open">
                        <span class="md-icon">open_in_new</span>
                    </button>
                    <button v-if="!file.isFolder" @click="downloadFile(file)" class="action-button download">
                        <span class="md-icon">download</span>
                    </button>
                    <button @click="deleteFile(file)" class="action-button delete">
                        <span class="md-icon">delete</span>
                    </button>
                </td>
            </tr>
            </tbody>
        </table>
    </div>
</template>

<script>

export default {
    name: "FilesView",
    props: {
        files: {
            type: Array,
            default: () => [],
        },
        selectedFiles: {
            type: Array,
            default: () => [],
        },
    },
    methods: {
        handleDoubleClick(file) {
            if (file.isFolder) {
                this.$emit("folderDoubleClick", file._id);
            } else if (this.isImage(file)) {
                this.showViewer(file);
            } else {
                this.openFile(file);
            }
        },
        getFileIcon(file) {
            if (file.isFolder) return "fas fa-folder";
            if (this.isImage(file)) return "fas fa-file-image";
            if (file.asset?.mime?.includes("pdf")) return "fas fa-file-pdf";
            return "fas fa-file";
        },
        getFileType(file) {
            if (file.isFolder) return "Klasör";
            if (file.asset?.mime) return file.asset.mime.split("/")[1].toUpperCase();
            return "Dosya";
        },
        isImage(file) {
            return file.asset?.mime?.includes("image");
        },
        getFileUrl(file) {
            return file.asset?.cdnUrl || "";
        },
        showViewer(file) {
            const url = this.getFileUrl(file);
            window.open(url, "_blank");
        },
        openFile(file) {
            console.log(`[DRIVE] File opened: ${file.name}`);
            this.$emit("fileOpen", file);
        },
        deleteFile(file) {
            console.log(`[DRIVE] File deleted: ${file.name}`);
            this.$emit("delete", file);
        },
        async downloadFile(file) {
            if (!file.isFolder && file.asset?.cdnUrl) {
                try {
                    const response = await fetch(file.asset.cdnUrl, {
                        method: "GET",
                        mode: "cors",
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);

                    const link = document.createElement("a");
                    link.href = blobUrl;
                    link.download = file.name;
                    link.style.display = "none";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    URL.revokeObjectURL(blobUrl);
                } catch (error) {
                    console.error("Fetch download failed, trying alternative methods:", error);

                    try {
                        const link = document.createElement("a");
                        link.href = file.asset.cdnUrl;
                        link.download = file.name;
                        link.target = "_blank";
                        link.rel = "noopener noreferrer";
                        link.style.display = "none";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        this.$message.success("Dosya yeni sekmede açıldı, indirme işlemi için sayfada sağ tıklayıp \"Resmi/Dosyayı kaydet\" seçeneğini kullanın");
                    } catch (fallbackError) {
                        console.error("All download methods failed:", fallbackError);

                        if (navigator.clipboard) {
                            try {
                                await navigator.clipboard.writeText(file.asset.cdnUrl);
                                this.$message.info("Dosya indirilemedi, URL panoya kopyalandı. Tarayıcıya yapıştırıp manuel olarak indirebilirsiniz.");
                            } catch (clipboardError) {
                                this.$message.error("Dosya indirilemedi. Lütfen daha sonra tekrar deneyin.");
                            }
                        } else {
                            this.$message.error("Dosya indirilemedi. Lütfen daha sonra tekrar deneyin.");
                        }
                    }
                }
            }
        },
        formatFileSize(size) {
            if (!size) return "N/A";
            const units = ["B", "KB", "MB", "GB"];
            let i = 0;
            while (size >= 1024 && i < units.length - 1) {
                size /= 1024;
                i++;
            }
            return `${size.toFixed(2)} ${units[i]}`;
        },
        formatDate(date) {
            if (!date) return "N/A";
            return new Date(date).toLocaleString();
        },
    },
};
</script>

<style scoped lang="scss">
.files-container {
    padding: 24px;
    background: transparent;
}

.files-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    font-size: 14px;
    table-layout: auto;
    border-radius: 16px;
    overflow: hidden;
}

.files-table thead {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
    
    th {
        font-weight: 600;
        color: #1e293b;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-size: 12px;
        padding: 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        
        &:first-child {
            border-top-left-radius: 16px;
        }
        
        &:last-child {
            border-top-right-radius: 16px;
        }
    }
}

.files-table td {
    padding: 16px;
    text-align: left;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    color: #475569;
    font-weight: 500;
    transition: all 0.2s ease;
    
    &:first-child {
        font-weight: 600;
        color: #1e293b;
    }
}

.files-table tr {
    transition: all 0.3s ease;
    
    &:hover {
        background: rgba(102, 126, 234, 0.05);
        transform: scale(1.01);
        
        td {
            color: #1e293b;
        }
    }
    
    &.selected {
        background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
        
        td {
            color: #667eea;
            font-weight: 600;
        }
    }
    
    &:last-child {
        td {
            &:first-child {
                border-bottom-left-radius: 16px;
            }
            
            &:last-child {
                border-bottom-right-radius: 16px;
            }
        }
    }
}

.file-icon {
    margin-right: 12px;
    cursor: pointer;
    font-size: 18px;
    transition: all 0.3s ease;
    
    &.fa-folder {
        color: #f59e0b;
    }
    
    &.fa-file-image {
        color: #8b5cf6;
    }
    
    &.fa-file-pdf {
        color: #ef4444;
    }
    
    &:hover {
        transform: scale(1.2);
    }
}

.actions-column {
    display: flex;
    gap: 8px;
    align-items: center;
}

.action-button {
    padding: 8px 12px;
    font-size: 12px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 4px;
    
    .md-icon {
        font-size: 16px;
    }
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
}

.action-button.open {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    }
}

.action-button.download {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    
    &:hover {
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }
}

.action-button.delete {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    
    &:hover {
        box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
    }
}
</style>
